import type {
  FactoryRunDisplay,
  FactoryRunRecord,
  FactoryStepRunDisplay,
  FactoryStepRunRecord
} from '@/types/factories'

function buildStepTree(stepRuns: FactoryStepRunRecord[]): FactoryStepRunDisplay[] {
  const byId = new Map<string, FactoryStepRunDisplay>()
  const roots: FactoryStepRunDisplay[] = []

  const sorted = [...stepRuns].sort((a, b) => a.sequence - b.sequence)
  sorted.forEach((stepRun) => {
    byId.set(stepRun.stepRunId, { ...stepRun, children: [] })
  })

  sorted.forEach((stepRun) => {
    const current = byId.get(stepRun.stepRunId)!
    if (stepRun.invoked.kind === 'fixer_agent' && stepRun.upstreamStepRunId) {
      const parent = byId.get(stepRun.upstreamStepRunId)
      if (parent) {
        parent.children = [...(parent.children || []), current]
        return
      }
    }
    roots.push(current)
  })

  return roots
}

export function toHistoricalFactoryRunDisplay(
  run: FactoryRunRecord,
  stepRuns: FactoryStepRunRecord[]
): FactoryRunDisplay {
  return {
    mode: 'historical',
    runId: run.runId,
    factoryConfigId: run.factoryConfigId,
    factoryName: run.factoryNameSnapshot,
    status: run.status,
    startedAt: run.startedAt,
    completedAt: run.completedAt,
    cancelledAt: run.cancelledAt,
    pausedAt: run.pausedAt,
    error: run.status === 'error' ? run.errorMessage || 'Factory run failed' : run.status === 'cancelled' ? 'Factory run cancelled' : null,
    usage: run.aggregateUsage,
    carryPayload: run.carryPayload,
    stepRuns: buildStepTree(stepRuns),
    perStepInvocationCounts: run.perStepInvocationCounts
  }
}

export function toIdleFactoryRunDisplay(factoryConfigId: string | null, factoryName: string): FactoryRunDisplay {
  return {
    mode: 'historical',
    runId: null,
    factoryConfigId,
    factoryName,
    status: 'idle',
    error: null,
    usage: null,
    carryPayload: '',
    stepRuns: [],
    perStepInvocationCounts: {}
  }
}

export function toErroredFactoryRunDisplay(
  factoryConfigId: string | null,
  factoryName: string,
  error: string
): FactoryRunDisplay {
  return {
    mode: 'live',
    runId: null,
    factoryConfigId,
    factoryName,
    status: 'error',
    error,
    usage: null,
    carryPayload: '',
    stepRuns: [],
    perStepInvocationCounts: {}
  }
}
